/**
 * @file 결재 API
 * 결재 문서의 생성·조회·처리(승인/반려/보류), 첨부파일 관리, 관리자 전체 조회 등을 처리합니다.
 */

import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const approvalApi = {
    /**
     * 상신 가능한 결재 양식 목록.
     *
     * 예전엔 프론트가 ApprovalType enum 7개를 하드코딩해 카드로 그렸다. 이제 양식은 DB 행이라
     * 관리자가 추가한 양식이 배포 없이 여기로 바로 나온다.
     *
     * 응답에는 카드 목록뿐 아니라 안내문구·필드 정의·결재선/열람자 기본값이 함께 온다.
     * 결재선은 서버가 소속팀장 조회와 스킵 규칙까지 적용해 '실제 사람'으로 내려주므로
     * 프론트가 규칙을 다시 구현하지 않는다 — 화면과 실제 결재선이 어긋날 자리를 없앤 것.
     */
    forms:            ()               => api.get(`${API_BASE}/approval/forms`),

    /** 목록 유형 필터용 — 사용 중지된 양식까지 전부. 상신용 forms() 와 기준이 다르다. */
    formOptions:      ()               => api.get(`${API_BASE}/approval/form-options`),

    /** 결재 문서 생성
     * @param {object} data - 결재 문서 데이터
     */
    create:           (data)           => api.post(`${API_BASE}/approval`, data),

    /** 수신 결재함 조회 (내가 결재해야 할 문서) */
    inbox:            ()               => api.get(`${API_BASE}/approval/inbox`),

    /** 전체 보기 — 서버가 «내가 올린 + 내 결재선» 을 합쳐 한 페이지로 준다.
     *  예전엔 세 함을 각각 size=9999 로 받아 화면에서 합쳤다(수백 KB · 정렬도 화면 몫). */
    all:              (page = 0, size = 20) => api.get(`${API_BASE}/approval/all`, { page, size }),

    /** 발신 결재함 조회 (페이지네이션)
     * @param {number} [page=0] - 페이지 번호 (0-based)
     * @param {number} [size=20] - 페이지 크기
     */
    sent:             (page = 0, size = 20) => api.get(`${API_BASE}/approval/sent`,      { page, size }),

    /** 관련 결재함 조회 (페이지네이션) */
    related:          (page = 0, size = 20) => api.get(`${API_BASE}/approval/related`,   { page, size }),

    /** 완료 결재함 조회 (페이지네이션) */
    completed:        (page = 0, size = 20) => api.get(`${API_BASE}/approval/completed`, { page, size }),

    /** 열람 결재함 — 내가 공람으로 지정된 문서. 결재 권한은 없고 보기·댓글만 가능하다. */
    viewing:          (page = 0, size = 20) => api.get(`${API_BASE}/approval/viewing`,   { page, size }),

    /** 아직 안 읽은 열람 문서 수 — 탭 배지용. */
    viewingUnread:    ()               => api.get(`${API_BASE}/approval/viewing/unread-count`),

    /** 결재 문서 상세 조회
     * @param {number} id - 결재 문서 ID
     */
    detail:           (id)             => api.get(`${API_BASE}/approval/${id}`),

    /** 결재 승인
     * @param {number} id - 결재 문서 ID
     * @param {string} comment - 승인 의견
     */
    approve:          (id, comment)    => api.post(`${API_BASE}/approval/${id}/approve`, { comment }),

    /** 결재 반려
     * @param {number} id - 결재 문서 ID
     * @param {string} comment - 반려 사유
     */
    reject:           (id, comment)    => api.post(`${API_BASE}/approval/${id}/reject`, { comment }),

    /** 결재 보류
     * @param {number} id - 결재 문서 ID
     * @param {string} comment - 보류 사유
     */
    hold:             (id, comment)    => api.post(`${API_BASE}/approval/${id}/hold`, { comment }),

    /** 결재 첨부파일 업로드 (multipart/form-data)
     * @param {number} id - 결재 문서 ID
     * @param {File} file - 업로드할 파일
     */
    uploadAttachment: (id, file)       => {
        const form = new FormData();
        form.append("file", file);
        return api.upload(`${API_BASE}/approval/${id}/attachment`, form);
    },

    /** 결재 첨부파일 삭제
     * @param {number} attachmentId - 첨부파일 ID
     */
    deleteAttachment: (attachmentId)   => api.delete(`${API_BASE}/approval/attachment/${attachmentId}`),

    /** 첨부 미리보기 (blob). PDF·이미지·엑셀·텍스트는 원본, 오피스는 서버 PDF 변환. */
    previewAttachment: (attachmentId)  => api.download(`${API_BASE}/approval/attachment/${attachmentId}/preview`),

    /** 결재 문서 삭제 (기안 취소)
     * @param {number} id - 결재 문서 ID
     */
    deleteApproval:   (id)             => api.delete(`${API_BASE}/approval/${id}`),

    /** 관리자용 전체 결재 목록 조회 (페이지네이션)
     * @param {string} status - 결재 상태 필터 ("ALL" 또는 특정 상태값)
     * @param {string} type - 결재 유형 필터 ("ALL" 또는 특정 유형값)
     * @param {number} [page=0] - 페이지 번호 (0-based)
     * @param {number} [size=20] - 페이지 크기
     */
    adminAll: (status, type, page = 0, size = 20, submitter = "", approver = "") => api.get(`${API_BASE}/approval/admin/all`, {
        ...(status && status !== "ALL" ? { status } : {}),
        ...(type   && type   !== "ALL" ? { type }   : {}),
        // 기안자/결재자 검색을 서버로 — 전체 결재에 걸쳐 매칭(현재 페이지만 필터하던 버그 해소)
        ...(submitter && submitter.trim() ? { submitter: submitter.trim() } : {}),
        ...(approver  && approver.trim()  ? { approver:  approver.trim()  } : {}),
        page,
        size,
    }),

    /** 관리자 강제 승인 — 결재선 무시 (APPROVAL_ADMIN) */
    adminApprove: (id, comment) => api.post(`${API_BASE}/approval/admin/${id}/approve`, { comment }),

    /** 관리자 강제 거절 — 결재선 무시 (APPROVAL_ADMIN) */
    adminReject:  (id, comment) => api.post(`${API_BASE}/approval/admin/${id}/reject`, { comment }),

    /** 상신한 결재 수정 — 저장하면 서버가 결재선을 1단계부터 다시 돌린다. */
    updateSubmitted: (id, payload) => api.put(`${API_BASE}/approval/${id}`, payload),

    /* ── 댓글 ─────────────────────────────────────────────────────────────────
       결재선의 «승인 사유» 와는 다른 것이다. 승인 사유는 결재자가 자기 차례에 한 번 남기는
       이력이고, 댓글은 문서를 볼 수 있는 사람이면 누구나 언제든 남기는 의견이다.
       («법인카드 영수증 첨부 부탁드립니다» 같은 요청이 오가는 자리 — 실제 양식 안내문구가
       "사용카드 번호가 변경된 경우에도 댓글로 메모 남겨주세요" 라고 이 사용을 명시한다.) */

    /** 댓글 목록 — 볼 수 있는 사람이면 누구나 */
    comments:      (id)            => api.get(`${API_BASE}/approval/${id}/comments`),

    /** 댓글 작성 */
    addComment:    (id, content)   => api.post(`${API_BASE}/approval/${id}/comments`, { content }),

    /** 댓글 수정 — 작성자 본인 또는 관리자 */
    editComment:   (commentId, content) => api.put(`${API_BASE}/approval/comments/${commentId}`, { content }),

    /** 댓글 삭제 — 작성자 본인 또는 관리자 */
    deleteComment: (commentId)     => api.delete(`${API_BASE}/approval/comments/${commentId}`),

    /** 댓글 첨부 업로드 — 댓글이 지워지면 함께 사라진다. */
    uploadCommentAttachment: (commentId, file) => {
        const form = new FormData();
        form.append("file", file);
        return api.upload(`${API_BASE}/approval/comments/${commentId}/attachment`, form);
    },

    /** 댓글 첨부 삭제 */
    deleteCommentAttachment: (attachmentId) =>
        api.delete(`${API_BASE}/approval/comments/attachment/${attachmentId}`),
};
